/**
 * @file DeviceConfigControlPanelPage.jsx
 * @description Config → Control Panel: the TCU fleet and its firmware.
 *
 *   Row 1  tiles — total TCUs, fitted, available in store, online in 24 h,
 *          up to date, older software, version not read.
 *   Row 2  the target version (defaults to the highest version recorded on
 *          any TCU; typing another is remembered in this browser), the
 *          version spread and the CAN mode split.
 *   Row 3  the update plan — every TCU below the target or with no version
 *          recorded, grouped into waves (rules: server/utils/tcuUpdatePlan.js).
 *
 * Data: GET /api/device-config/control-panel?target= (60 s server cache).
 * Nothing on this page sends anything to a device — the over-the-air update
 * command is not built yet. A missing version is filled in on the device page
 * (Firmware (SV)), which the Device ID links to.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Typography, Stack, Card, Chip, Button, IconButton, Tooltip, TextField,
    Alert, Skeleton, Link, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow,
} from '@mui/material';
import {
    Refresh as RefreshIcon, Memory as TcuIcon, Build as FittedIcon,
    Inventory2 as StoreIcon, Wifi as OnlineIcon, CheckCircle as UpToDateIcon,
    Update as OlderIcon, HelpOutline as NotReadIcon,
} from '@mui/icons-material';
import deviceConfigService from '../../../services/deviceConfigService';
import { formatToIST } from '../../../utils/dataUtils';
import { statusLabel, statusColor } from '../../../utils/statusLabels';
import { machineSerial, machineEquipmentId } from '../../../utils/machineLabel';

const TARGET_KEY = 'configControlPanel.targetVersion';

const readStoredTarget = () => {
    try { return localStorage.getItem(TARGET_KEY) || ''; } catch { return ''; }
};

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));

/** "35 min ago" / "4 h ago" / "11 d ago" — how long since the last frame. */
const ago = (ts) => {
    if (!ts) return '';
    const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
    if (!Number.isFinite(mins) || mins < 0) return '';
    if (mins < 60) return `${mins} min ago`;
    if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
    return `${Math.round(mins / 1440)} d ago`;
};

const VS_TARGET = {
    target: { label: 'Target', color: 'success' },
    newer: { label: 'Newer', color: 'success' },
    older: { label: 'Older', color: 'warning' },
};

/** One headline number; the accent bar and icon carry the tile's colour, the label says what it is. */
const Tile = ({ icon, label, value, sub, color }) => (
    <Card variant="outlined" sx={{ p: 2, position: 'relative', overflow: 'hidden', borderRadius: '14px' }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: color }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>{label}</Typography>
            <Box sx={{ color, display: 'flex', '& svg': { fontSize: 20 } }}>{icon}</Box>
        </Stack>
        <Typography variant="h4" sx={{ mt: 0.5 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>{sub}</Typography>}
    </Card>
);

/** Labelled horizontal bar — count against the largest bucket in the list. */
const BarRow = ({ label, count, max, chip, fill = 'primary.main' }) => (
    <Box sx={{ py: 0.75 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', flex: 1, minWidth: 0 }} noWrap>{label}</Typography>
            {chip}
            <Typography variant="body2" fontWeight={700} className="tnum">{fmt(count)}</Typography>
        </Stack>
        <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.selected' }}>
            <Box sx={{ height: 8, borderRadius: 4, bgcolor: fill, width: `${max ? Math.max(2, (count / max) * 100) : 0}%` }} />
        </Box>
    </Box>
);

const SectionCard = ({ title, caption, children, sx }) => (
    <Card variant="outlined" sx={{ p: 2.5, borderRadius: '14px', ...sx }}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        {caption && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>{caption}</Typography>}
        {children}
    </Card>
);

const DeviceConfigControlPanelPage = () => {
    const [target, setTarget] = useState(readStoredTarget);   // applied target ('' = highest recorded)
    const [draft, setDraft] = useState(readStoredTarget);     // what is typed in the box
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await deviceConfigService.getControlPanel(target);
            setData(r);
            setError('');
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Could not load the control panel');
        } finally {
            setLoading(false);
        }
    }, [target]);

    useEffect(() => { load(); }, [load]);

    const applyTarget = (value) => {
        const v = String(value || '').trim();
        try {
            if (v) localStorage.setItem(TARGET_KEY, v);
            else localStorage.removeItem(TARGET_KEY);
        } catch { /* storage unavailable — the target still applies for this visit */ }
        setDraft(v);
        setTarget(v);
    };

    const t = data?.totals || {};
    const fw = data?.firmware || {};
    const plan = data?.plan || { waves: [], rows: [] };
    const versions = fw.versions || [];
    const canModes = data?.can_modes || [];
    const versionMax = Math.max(0, ...versions.map(v => v.count), fw.not_read || 0);
    const canMax = Math.max(0, ...canModes.map(c => c.count));
    const excluded = data?.excluded || {};
    const excludedNote = [
        excluded.sinoboom_machines ? `${excluded.sinoboom_machines} Sinoboom (i-Link)` : null,
        excluded.test_devices ? `${excluded.test_devices} bench/test` : null,
    ].filter(Boolean).join(' · ');

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} className="animate-fade-in">
            {/* Refresh — no page title, the top bar already names the page */}
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    {data?.generated_at && (
                        <Typography variant="caption" color="text.secondary">
                            Updated {formatToIST(data.generated_at, 'dd MMM, HH:mm')} IST
                        </Typography>
                    )}
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton size="small" onClick={load} disabled={loading} aria-label="Refresh">
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
                {data?.ota?.note || 'This panel plans and tracks only — it sends nothing. The over-the-air update command is not built yet; its home will be the Device Config page.'}
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}
                    action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
                    {error}
                </Alert>
            )}
            {(data?.warnings || []).map(w => <Alert key={w} severity="warning" sx={{ mb: 2 }}>{w}</Alert>)}

            {/* Row 1 — tiles */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', lg: 'repeat(7, 1fr)' }, gap: 1.5, mb: 2 }}>
                {loading && !data ? (
                    Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} variant="rectangular" height={104} sx={{ borderRadius: '14px' }} />)
                ) : data ? (
                    <>
                        <Tile icon={<TcuIcon />} label="Total TCUs" value={fmt(t.tcus)} color="primary.main"
                            sub={excludedNote ? `Not counted: ${excludedNote}` : 'Autopeepal units we own'} />
                        <Tile icon={<FittedIcon />} label="Fitted" value={fmt(t.fitted)} color="primary.main" sub="Mapped to a machine" />
                        <Tile icon={<StoreIcon />} label="Available in store" value={fmt(t.in_store)} color="info.main" sub="Registered, awaiting installation" />
                        <Tile icon={<OnlineIcon />} label="Online 24 h" value={fmt(t.online_24h)} color="success.main"
                            sub={t.offline == null ? 'Last frame not readable' : `${fmt(t.offline)} offline${t.never_reported ? ` · ${fmt(t.never_reported)} never reported` : ''}`} />
                        <Tile icon={<UpToDateIcon />} label="Up to date" value={fmt(fw.up_to_date)} color="success.main"
                            sub={fw.target ? `On ${fw.target} or newer` : 'No target yet'} />
                        <Tile icon={<OlderIcon />} label="Older software" value={fmt(fw.older)} color="warning.main" sub="Below the target" />
                        <Tile icon={<NotReadIcon />} label="Version not read" value={fmt(fw.not_read)} color="text.disabled" sub="No firmware recorded" />
                    </>
                ) : null}
            </Box>

            {/* Row 2 — target, versions, CAN mode */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1.2fr 1fr' }, gap: 1.5, mb: 2 }}>
                <SectionCard title="Target version"
                    caption="A TCU at or above this version is up to date. Left blank, the target is the highest version recorded on any TCU.">
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField size="small" fullWidth label="Target version" value={draft}
                            placeholder={fw.highest_recorded || 'e.g. 1.4.2'}
                            onChange={e => setDraft(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') applyTarget(draft); }}
                            inputProps={{ style: { fontFamily: 'monospace' }, maxLength: 64 }} />
                        <Button variant="contained" onClick={() => applyTarget(draft)}
                            disabled={loading || draft.trim() === target}>
                            Apply
                        </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        {fw.highest_recorded
                            ? <>Highest recorded on any TCU: <Box component="b" sx={{ fontFamily: 'monospace' }}>{fw.highest_recorded}</Box></>
                            : (data ? 'No firmware version is recorded on any TCU yet.' : '')}
                    </Typography>
                    {data && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">Planning against:</Typography>
                            {fw.target
                                ? <Chip size="small" color="primary" variant="outlined" sx={{ fontFamily: 'monospace' }}
                                    label={`${fw.target} · ${fw.target_source === 'query' ? 'typed here' : 'highest recorded'}`} />
                                : <Chip size="small" variant="outlined" label="No target — nothing recorded" />}
                        </Stack>
                    )}
                    {target && (
                        <Button size="small" sx={{ mt: 1, px: 0 }} onClick={() => applyTarget('')}>
                            Use the highest recorded instead
                        </Button>
                    )}
                </SectionCard>

                <SectionCard title="Firmware in the fleet"
                    caption={data && fw.not_read
                        ? `${fmt(fw.not_read)} of ${fmt(t.tcus)} TCUs have no version recorded. Open one from the plan (Device ID) and fill in Firmware (SV).`
                        : 'Versions as recorded on each TCU (Firmware (SV) on the device page).'}>
                    {loading && !data ? <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                        : versions.length === 0 && !fw.not_read ? (
                            <Typography variant="body2" color="text.secondary">No TCUs to show.</Typography>
                        ) : (
                            <>
                                {versions.map(v => (
                                    <BarRow key={v.version} label={v.version} count={v.count} max={versionMax}
                                        chip={<Chip size="small" color={VS_TARGET[v.vs_target].color} label={VS_TARGET[v.vs_target].label} />} />
                                ))}
                                {fw.not_read > 0 && (
                                    <BarRow label="Not read yet" count={fw.not_read} max={versionMax} fill="text.disabled"
                                        chip={<Chip size="small" variant="outlined" label="No version" />} />
                                )}
                            </>
                        )}
                </SectionCard>

                <SectionCard title="CAN mode"
                    caption="As last set from Device Config (SET CANM). “Not recorded” = never set through the command channel, not an unknown mode on the unit.">
                    {loading && !data ? <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                        : canModes.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No TCUs to show.</Typography>
                        ) : canModes.map(c => (
                            <BarRow key={c.mode || 'none'} label={c.mode || 'Not recorded'} count={c.count} max={canMax}
                                fill={c.mode ? 'primary.main' : 'text.disabled'} />
                        ))}
                </SectionCard>
            </Box>

            {/* Row 3 — the update plan */}
            <SectionCard title={`Update plan${data ? ` — ${fmt(plan.rows.length)} TCU${plan.rows.length === 1 ? '' : 's'}` : ''}`}
                caption="Every TCU below the target or with no version recorded, in the order to update them.">
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: `repeat(${Math.max(plan.waves.length, 4)}, 1fr)` }, gap: 1, mb: 2 }}>
                    {plan.waves.map(w => (
                        <Box key={w.key} sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: w.count ? 'background.paper' : 'action.hover' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                <Typography variant="overline" color="text.secondary">{w.wave ? `Wave ${w.wave}` : 'Not planned'}</Typography>
                                <Typography variant="h6" className="tnum">{fmt(w.count)}</Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={700}>{w.title}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">{w.rule}</Typography>
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Window: {w.window}</Typography>
                        </Box>
                    ))}
                </Box>

                {loading && !data ? (
                    [0, 1, 2].map(i => <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 2 }} />)
                ) : !data ? null : t.tcus === 0 ? (
                    <Box sx={{ p: 5, textAlign: 'center' }}>
                        <TcuIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="h6" color="text.secondary">No TCUs registered</Typography>
                        <Typography variant="body2" color="text.secondary">Register a unit under Hardware → Registration and it appears here.</Typography>
                    </Box>
                ) : plan.rows.length === 0 ? (
                    <Box sx={{ p: 5, textAlign: 'center' }}>
                        <UpToDateIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                        <Typography variant="h6" color="text.secondary">Every TCU is on {fw.target} or newer</Typography>
                        <Typography variant="body2" color="text.secondary">Nothing to plan against this target.</Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{ maxHeight: '70vh', border: 1, borderColor: 'divider', borderRadius: 2 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Machine</TableCell>
                                    <TableCell>Device ID</TableCell>
                                    <TableCell>Firmware</TableCell>
                                    <TableCell>CAN mode</TableCell>
                                    <TableCell>Last frame (IST)</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Update window</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {plan.waves.filter(w => w.count > 0).map(w => (
                                    <React.Fragment key={w.key}>
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ bgcolor: 'action.hover', py: 1 }}>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {w.wave ? `Wave ${w.wave} · ` : ''}{w.title} — {fmt(w.count)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {plan.rows.filter(r => r.wave === w.wave).map(r => {
                                            const serial = machineSerial(r);
                                            return (
                                                <TableRow key={r.device_id} hover>
                                                    <TableCell>
                                                        {r.equipment_id ? (
                                                            <>
                                                                <Typography variant="body2" fontWeight={700}>{serial || machineEquipmentId(r)}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {[r.model, serial ? null : 'no serial on record'].filter(Boolean).join(' · ') || '—'}
                                                                </Typography>
                                                            </>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">In store — no machine</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip describeChild title="Open the device page — Firmware (SV) is filled in there">
                                                            <Link component={RouterLink} to={`/mewp-data/${encodeURIComponent(r.device_id)}`}
                                                                variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                {r.device_id}
                                                            </Link>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>
                                                        {r.version ? (
                                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.version}</Typography>
                                                                <Chip size="small" color="warning" label="Older" />
                                                            </Stack>
                                                        ) : (
                                                            <Chip size="small" variant="outlined" label="Not read yet" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color={r.can_mode ? 'text.primary' : 'text.secondary'}>
                                                            {r.can_mode || 'Not recorded'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {r.last_seen ? (
                                                            <>
                                                                <Typography variant="body2">{formatToIST(r.last_seen, 'dd MMM yyyy, HH:mm')}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{ago(r.last_seen)}</Typography>
                                                            </>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {r.online == null ? 'Not readable' : 'Never reported'}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {r.equipment_id
                                                            ? (r.status
                                                                ? <Chip size="small" color={statusColor(r.status)} label={statusLabel(r.status)} />
                                                                : <Typography variant="body2" color="text.secondary">Not set</Typography>)
                                                            : <Typography variant="body2" color="text.secondary">—</Typography>}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600}>{r.window}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{r.reason}</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </SectionCard>
        </Box>
    );
};

export default DeviceConfigControlPanelPage;
